/**
 * Groups a flat array of surveys into parent-child structure.
 *
 * Parent surveys have parentid === null.
 * Child surveys have parentid === <parent's UUID>.
 *
 * Returns an array of parent survey objects, each with:
 *   - children: array of child surveys
 *   - hasChildren: boolean
 *
 * Surveys that are children whose parent is NOT in the array
 * (e.g., parent is on another page) are shown as standalone rows.
 */
export function groupSurveysByParent(surveys: any[]): any[] {
  if (!surveys || !Array.isArray(surveys) || surveys.length === 0) return [];

  const parents: any[] = [];
  const children: any[] = [];

  surveys.forEach((s) => {
    if (!s.parentid) {
      parents.push(s);
    } else {
      children.push(s);
    }
  });

  const childMap: Record<string, any[]> = {};
  const orphans: any[] = [];
  const parentIds = new Set(parents.map((p) => p.id));

  children.forEach((child) => {
    if (parentIds.has(child.parentid)) {
      if (!childMap[child.parentid]) {
        childMap[child.parentid] = [];
      }
      childMap[child.parentid].push(child);
    } else {
      orphans.push(child);
    }
  });

  const grouped = parents.map((parent) => ({
    ...parent,
    children: childMap[parent.id] || [],
    hasChildren: (childMap[parent.id] || []).length > 0,
  }));

  // Orphan children (parent not on this page) shown as standalone rows
  orphans.forEach((orphan) => {
    grouped.push({ ...orphan, children: [], hasChildren: false });
  });

  return grouped;
}
